import React, { useState, useEffect } from 'react';
import { Box, Typography, Modal, TextField, Button, Grid, MenuItem, FormControl, InputLabel, Select, IconButton, CircularProgress, FormHelperText } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import { StaffService } from '../api/services/staffService';
import { toast } from '../utils/toast';

const formatDateTimeLocal = (date) => {
    const d = date ? new Date(date) : new Date();
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export default function RoomOOOModal({ open, onClose, room, onConfirm, isExtend = false, isBulk = false }) {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        startDate: '',
        endDate: '',
        reason: 'Maintenance',
        assignedStaffId: '',
        notes: ''
    });

    const [staffList, setStaffList] = useState([]);
    const [isLoadingStaff, setIsLoadingStaff] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) {
            setFormData({
                startDate: '',
                endDate: '',
                reason: 'Maintenance',
                assignedStaffId: '',
                notes: ''
            });
            setErrors({});
            fetchStaff();
        } else {
            setFormData({
                startDate: '',
                endDate: '',
                reason: 'Maintenance',
                assignedStaffId: '',
                notes: ''
            });
            setErrors({});
        }
    }, [open, isExtend, room]);

    const fetchStaff = async () => {
        setIsLoadingStaff(true);
        try {
            const res = await StaffService.getStaff({ noPagination: true });
            const list = Array.isArray(res)
                ? res
                : (Array.isArray(res?.data)
                    ? res.data
                    : (Array.isArray(res?.staff)
                        ? res.staff
                        : (Array.isArray(res?.data?.staff)
                            ? res.data.staff
                            : (Array.isArray(res?.staffMembers)
                                ? res.staffMembers
                                : []))));
            setStaffList(list.filter(s => !s.status || s.status.toLowerCase() === 'active'));
        } catch (err) {
            console.error("Failed to load staff for OOO modal:", err);
        } finally {
            setIsLoadingStaff(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }

        // Real-time comparison clearance if user fixes date relation
        if (name === 'startDate' && formData.endDate && value) {
            if (new Date(formData.endDate) > new Date(value)) {
                setErrors(prev => ({ ...prev, endDate: null, startDate: null }));
            }
        }
        if (name === 'endDate' && formData.startDate && value) {
            if (new Date(value) > new Date(formData.startDate)) {
                setErrors(prev => ({ ...prev, endDate: null, startDate: null }));
            }
        }
    };

    const handleSubmit = async () => {
        const newErrors = {};

        if (isExtend) {
            if (!formData.endDate) {
                newErrors.endDate = "End date is required";
            } else {
                const end = new Date(formData.endDate);
                if (isNaN(end.getTime())) {
                    newErrors.endDate = "Invalid end date & time";
                } else {
                    const currentEndDateStr = room?.oooEndDate || room?.activeOOO?.endDate || room?.activeOOORecord?.endDate || (room?.outOfOrders && room?.outOfOrders.find(o => o.status === 'Active')?.endDate);
                    if (currentEndDateStr && end <= new Date(currentEndDateStr)) {
                        newErrors.endDate = "Extended end date must be after the current end date";
                    }
                }
            }
        } else {
            if (!formData.startDate) {
                newErrors.startDate = "Start date & time is required";
            } else if (isNaN(new Date(formData.startDate).getTime())) {
                newErrors.startDate = "Invalid start date & time";
            }

            if (!formData.endDate) {
                newErrors.endDate = "End date & time is required";
            } else if (isNaN(new Date(formData.endDate).getTime())) {
                newErrors.endDate = "Invalid end date & time";
            }

            if (formData.startDate && formData.endDate) {
                const start = new Date(formData.startDate);
                const end = new Date(formData.endDate);
                if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end <= start) {
                    newErrors.endDate = "End time must be after start time";
                }
            }

            if (!formData.reason) newErrors.reason = "Reason is required";
            if (!formData.assignedStaffId) newErrors.assignedStaffId = "Staff assignment is required";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            const firstErrorMsg = newErrors.endDate || newErrors.startDate || newErrors.assignedStaffId || newErrors.reason || "Please fill all required fields correctly";
            toast.error(firstErrorMsg);
            return;
        }

        setLoading(true);
        try {
            const payload = isExtend ? {
                endDate: new Date(formData.endDate).toISOString(),
                ...(formData.assignedStaffId ? { assignedStaffId: formData.assignedStaffId } : {})
            } : {
                startDate: new Date(formData.startDate).toISOString(),
                endDate: new Date(formData.endDate).toISOString(),
                reason: formData.reason,
                assignedStaffId: formData.assignedStaffId,
                notes: formData.notes
            };
            
            if (onConfirm) {
                if (isBulk) {
                    await onConfirm(null, payload, false, true);
                } else {
                    await onConfirm(room.id, payload, isExtend, false);
                }
            }
            handleClose();
        } catch (error) {
            console.error("Failed to set room out-of-order:", error);
            toast.error(error.message || "Failed to set room out-of-order");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            startDate: '',
            endDate: '',
            reason: 'Maintenance',
            assignedStaffId: '',
            notes: ''
        });
        setErrors({});
        onClose();
    };

    const handleModalClose = (event, reason) => {
        if (reason === 'backdropClick') return;
        if (loading) return;
        handleClose();
    };

    return (
        <Modal open={open} onClose={handleModalClose}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '92%', sm: 520 },
                maxHeight: '90vh',
                overflowY: 'auto',
                bgcolor: '#ffffff',
                borderRadius: 4,
                p: { xs: 2.5, sm: 3.5 },
                boxShadow: '0 24px 48px rgba(0,0,0,0.14)',
                border: '1px solid rgba(0,0,0,0.06)',
                outline: 'none',
            }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 42,
                            height: 42,
                            borderRadius: '12px',
                            bgcolor: 'rgba(211, 47, 47, 0.08)',
                            color: 'error.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <BuildCircleOutlinedIcon />
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight="700" sx={{ lineHeight: 1.2 }}>
                                {isBulk 
                                    ? `Set ${room?.length || 0} Rooms Out-of-Order` 
                                    : (isExtend ? `Extend Room ${room?.roomNumber} Out-of-Order` : `Set Room ${room?.roomNumber} Out-of-Order`)
                                }
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Block room inventory and assign maintenance staff
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        disabled={loading}
                        sx={{
                            color: 'text.secondary',
                            bgcolor: 'rgba(0,0,0,0.04)',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Grid container spacing={2}>
                    {!isExtend && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label="Start Date & Time "
                                type="datetime-local"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    min: formatDateTimeLocal(new Date())
                                }}
                                error={!!errors.startDate}
                                helperText={errors.startDate}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={isExtend ? 12 : 6}>
                        <TextField
                            fullWidth
                            size="small"
                            required
                            label="End Date & Time "
                            type="datetime-local"
                            name="endDate"
                            value={formData.endDate}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                min: formData.startDate || formatDateTimeLocal(new Date())
                            }}
                            error={!!errors.endDate}
                            helperText={errors.endDate}
                        />
                    </Grid>
                    {!isExtend && (
                        <>
                            <Grid item xs={12}>
                                <FormControl fullWidth size="small" required error={!!errors.reason}>
                                    <InputLabel>Reason </InputLabel>
                                    <Select
                                        name="reason"
                                        value={formData.reason}
                                        label="Reason *"
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="Maintenance">Maintenance</MenuItem>
                                        <MenuItem value="Cleaning">Deep Cleaning</MenuItem>
                                        <MenuItem value="Renovation">Renovation</MenuItem>
                                        <MenuItem value="Repair">Emergency Repair</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                    </Select>
                                    {errors.reason && <FormHelperText>{errors.reason}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth size="small" required error={!!errors.assignedStaffId}>
                                    <InputLabel>Assigned Staff </InputLabel>
                                    <Select
                                        name="assignedStaffId"
                                        value={formData.assignedStaffId}
                                        label="Assigned Staff *"
                                        onChange={handleChange}
                                    >
                                        {staffList.map((s) => (
                                            <MenuItem key={s.id || s._id} value={s.id || s._id}>
                                                {s.fullName || s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.email}
                                            </MenuItem>
                                        ))}
                                        {staffList.length === 0 && <MenuItem disabled>No active staff available</MenuItem>}
                                    </Select>
                                    {errors.assignedStaffId && <FormHelperText>{errors.assignedStaffId}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Internal Notes"
                                    name="notes"
                                    multiline
                                    rows={3}
                                    placeholder="e.g. A/C Repair, Plumbing, Painting, etc."
                                    value={formData.notes}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>

                <Box sx={{ display: 'flex', gap: 2, mt: 3.5 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            py: 1.1,
                            borderRadius: 2.5,
                            textTransform: 'none',
                            fontWeight: '600',
                            color: '#475569',
                            borderColor: '#cbd5e1',
                            '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        fullWidth 
                        variant="contained" 
                        color="error" 
                        onClick={handleSubmit} 
                        disabled={loading || isLoadingStaff}
                        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{
                            py: 1.1,
                            borderRadius: 2.5,
                            textTransform: 'none',
                            fontWeight: '700',
                            boxShadow: 'none',
                            '&:hover': { boxShadow: '0 4px 12px rgba(211, 47, 47, 0.24)' },
                        }}
                    >
                        {loading ? 'Processing...' : (isExtend ? 'Extend Period' : 'Set Out-of-Order')}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
